import { useCallback, useEffect, useState } from "react";
import { useCreateDebounceHandler } from "../../../../common/hooks/use_debounced_callback";
import { processError } from "../../../../common/utils/base_error";
import { useNavigate } from "react-router";
import { EmpreendimentoLista } from "../../domain/models/empreendimento_lista";
import { useEmpreendimentoListaPagination } from "../hooks/empreendimento_lista/pagination";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";

type LoadEmpreendimentoListaState = {
    page?: number;
    rowsPerPage?: number;
}

export const useEmpreendimentoListaViewModel = () => {
    const {
        incorporacao: {
            getAllEmpreendimentoListaUseCase,
            deleteEmpreendimentoUseCase
        },
    } = useDependencyInjection();

    const { pushErrorNotification } = usePageLoader();
    const backOrNavigate = useBackOrNavigate()

    /** Estado responsável para definir um delay para a ativação de uma função.
     * Parametro: tempo de delay em milisegundos.
     */
    const debouncedRef = useCreateDebounceHandler(750);
    /**
     * Estados relacionados aos modais de cadastro e de deleção de centro custo.
     */
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [selectedEmpreendimento, setSelectedEmpreendimento] = useState<EmpreendimentoLista>();
    const navigate = useNavigate();
    /**
     * Usado para o processo de carregamento.
     */
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    /**
     * Estado de erro que impede o carregamento da página.
     */
    const [errorMessage, setErrorMessage] = useState<string>();

    /**
     * Estado de erro que ocorre após o carregamento da página.
     */
    const [empreendimentoLista, setEmpreendimentoLista] = useState<EmpreendimentoLista[]>()

    /**
     * Estados globais da aplicação criados pelo zustand.
     * Filtros: Armazena os estados e funções dos campos de filtro da pesquisa.
     * Pagination: Armazena os estados e funções de páginação da tabela.
     */
    // const filters = useCentroCustoListaFilters();
    const pagination = useEmpreendimentoListaPagination();


    /**
     * Funções de carregamento
     */


    /**
     * Função otimizada e eficiente para controle de estado dos dados.
     * Função responsável em controlar a atualização dos dados sempre mantendo em 
     * atualizados.
     * @param funcState model criado para consulta da lista de centro de custos.
     */
    const LoadEmpreendimentoListaState = useCallback(async (funcState?: LoadEmpreendimentoListaState) => {
        try {

            const filteredPage = funcState?.page ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllEmpreendimentoListaUseCase.getAllEmprendimentoLista(
                filteredPage,
                filteredRowsPerPage,
            );

            setEmpreendimentoLista(getAllEmpreendimentoListaUseCase.items);

            pagination.setRowsPerPage(getAllEmpreendimentoListaUseCase.pageSize);
            pagination.setTotalItems(getAllEmpreendimentoListaUseCase.totalItems);
        } catch (e: unknown) {
            setEmpreendimentoLista([]);
            throw new Error("Falha ao buscar dados de empreendimento!");
        }
    }, [
        //filters,
        pagination,
        getAllEmpreendimentoListaUseCase,
    ]);

    /**
     * useEffect
     * Apenas carregamento inicial dos estados.
     */

    useEffect(() => {
        (async function () {
            if (!loading && !errorMessage) {
                try {
                    setLoading(true);
                    setErrorMessage(undefined);

                    if (empreendimentoLista === undefined) {
                        await LoadEmpreendimentoListaState();
                    }
                } catch (e: unknown) {
                    setErrorMessage(processError(e, "Falha ao buscar lista de empreendimento!")[0]);
                } finally {
                    setLoading(false);
                }
            }
        })();
    }, [
        loading,
        errorMessage,
        empreendimentoLista,
        LoadEmpreendimentoListaState,
    ]);

    /**
    * Eventos
    * Eventos desparados pela View.
    */


    const handleReload = useCallback(async () => {
        try {
            setLoading(true);
            await LoadEmpreendimentoListaState();
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar lista de centro custo!'));
        } finally {
            setLoading(false);
        }
    }, [
        LoadEmpreendimentoListaState,
        pushErrorNotification,
    ]);


    /**
     * Evento disparado ao mudar de página.
     */
    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);

            pagination.setPage(page + 1);

            await LoadEmpreendimentoListaState({ page: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar lista de empreendimento'));
        } finally {
            setLoading(false);
        }
    }, [
        pagination,
        LoadEmpreendimentoListaState,
        pushErrorNotification,
    ]);

    /**
     * Evento disparado ao mudar o número de itens por página.
     */
    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);

            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);

            await LoadEmpreendimentoListaState({ rowsPerPage, page: 1 });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar empreendimentos!'));
        } finally {
            setLoading(false);
        }
    }, [
        pagination,
        LoadEmpreendimentoListaState,
        pushErrorNotification,
    ]);

    // const handleEmpreendimentoEdit = useCallback((idEmpreendimento: number | null) => {
    //     navigate('/Empreendimento_cadastro', { state: { idEmpreendimento } })
    // }, [navigate]);

    const handleEmpreendimentoEdit = useCallback(async (idEmpreendimento: number, openNewTab: boolean) => {
        if (openNewTab) {
            window.open(`/Empreendimento_cadastro?id=${idEmpreendimento}`, "_blank", "noopener,noreferrer");;
        } else {
            backOrNavigate(`/Empreendimento_cadastro?id=${idEmpreendimento}`, { alternativeRoute: '/Empreendimento_lista' });
        }
    }, [
        backOrNavigate,
    ]);

    const handleNewEmpreendimento = useCallback(() => {
        navigate("/Empreendimento_cadastro");
    }, [navigate]);

    const handleDeleteEmpreendimentoOpenState = useCallback((idEmpreendimento: number | null, openState: boolean) => {
        if (openState) {
            if (idEmpreendimento && empreendimentoLista) {
                setSelectedEmpreendimento(empreendimentoLista.find(x => x.idEmpreendimento === idEmpreendimento));
            } else {
                setSelectedEmpreendimento(undefined);
            }
        }
        setOpenDeleteModal(openState);
    }, [empreendimentoLista]);

    const handleConfirmarDeletarEmpreendimento = useCallback(async () => {
        try {
            if (selectedEmpreendimento) {
                setLoading(true);

                await deleteEmpreendimentoUseCase.deleteEmpreendimento(selectedEmpreendimento.idEmpreendimento)
                await LoadEmpreendimentoListaState();
            }
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao deletar Bloco!')[0]);
        } finally {
            setLoading(false);
            setOpenDeleteModal(false);
        }
    }, [
        selectedEmpreendimento,
        deleteEmpreendimentoUseCase,
        LoadEmpreendimentoListaState,
        pushErrorNotification,
    ]);


    return {
        loading,
        saving,
        empreendimentoLista: empreendimentoLista ?? [],
        errorMessage,
        pagination,
        openDeleteModal,
        selectedEmpreendimento,
        handleReload,
        handlePageChange,
        handleRowsPerPageChange,
        handleNewEmpreendimento,
        handleEmpreendimentoEdit,
        handleDeleteEmpreendimentoOpenState,
        handleConfirmarDeletarEmpreendimento

    };
}
